import { useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Html, Line } from '@react-three/drei';
import { annulusShape, extrudeOpts } from './wallShape.js';

const CABLE_COLOR_HEX = {
  BLUE: '#3B82F6', BROWN: '#8B4513', 'GREEN-YELLOW': '#9ACD32', GREY: '#808080',
  BLACK: '#333333', WHITE: '#EEEEEE', RED: '#EF4444', YELLOW: '#F59E0B',
  GREEN: '#22C55E', ORANGE: '#F97316'
};
const COPPER = '#B87333';
const insulationHex = c => CABLE_COLOR_HEX[String(c || '').toUpperCase()] || '#D9D2C5';
const tag = color => ({
  color, fontSize: 10, fontFamily: 'monospace', whiteSpace: 'nowrap',
  background: 'rgba(15,20,32,0.85)', padding: '1px 4px', borderRadius: 3, pointerEvents: 'none'
});

// one insulated conductor: copper core + a variable-wall insulation sleeve
// (extruded from the 4-side wall shape), with a solid front cross-section.
function Conductor({ core, x, len, annotate, label }) {
  const copperR = core.copperD / 2;
  const sides = core.wall.sides;
  const capZ = len / 2;
  const shape = useMemo(() => annulusShape(copperR, sides, 96), [copperR, sides.top, sides.right, sides.bottom, sides.left]);

  const radialLines = [
    { p1: [0, copperR], p2: [0, copperR + sides.top], lp: [0, copperR + sides.top + 0.15], v: sides.top, k: 'top' },
    { p1: [copperR, 0], p2: [copperR + sides.right, 0], lp: [copperR + sides.right + 0.35, 0], v: sides.right, k: 'right' },
    { p1: [0, -copperR], p2: [0, -(copperR + sides.bottom)], lp: [0, -(copperR + sides.bottom) - 0.15], v: sides.bottom, k: 'bottom' },
    { p1: [-copperR, 0], p2: [-(copperR + sides.left), 0], lp: [-(copperR + sides.left) - 0.35, 0], v: sides.left, k: 'left' }
  ];

  return (
    <group position={[x, 0, 0]}>
      {/* variable-wall insulation, translucent so copper + wall variation read */}
      <mesh position={[0, 0, -len / 2]}>
        <extrudeGeometry args={[shape, extrudeOpts(len)]} />
        <meshStandardMaterial color={insulationHex(core.color)} transparent opacity={0.5} roughness={0.6} side={2} />
      </mesh>
      {/* solid front cap of the same ring */}
      <mesh position={[0, 0, capZ]}>
        <extrudeGeometry args={[shape, extrudeOpts(0.02)]} />
        <meshStandardMaterial color={insulationHex(core.color)} roughness={0.7} side={2} />
      </mesh>
      {/* copper core */}
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[copperR, copperR, len * 1.04, 40]} />
        <meshStandardMaterial color={COPPER} metalness={0.85} roughness={0.35} />
      </mesh>

      {annotate && (
        <group position={[0, 0, capZ + 0.03]}>
          {radialLines.map(r => (
            <group key={r.k}>
              <Line points={[[...r.p1, 0], [...r.p2, 0]]} color="#ffd166" lineWidth={2} />
              <Html position={[...r.lp, 0]} center>
                <div style={tag('#ffd166')}>{r.k} {r.v.toFixed(2)}</div>
              </Html>
            </group>
          ))}
        </group>
      )}
      <Html position={[0, (core.outerD / 2) + 0.4, capZ]} center>
        <div style={{ color: insulationHex(core.color), fontSize: 11, fontFamily: 'monospace', pointerEvents: 'none', fontWeight: 700 }}>{label}</div>
      </Html>
    </group>
  );
}

const ConductorIsolation3D = ({ model, height = 440 }) => {
  const cores = model?.cores || [];
  if (!cores.length) {
    return <div style={{ height, background: '#0f1420', borderRadius: 6 }}
      className="d-flex align-items-center justify-content-center text-500">No conductor data</div>;
  }
  const maxR = Math.max(...cores.map(c => c.outerD / 2));
  const gap = maxR * 2.8;
  const len = maxR * 3;
  const totalW = gap * (cores.length - 1);
  const cam = Math.max(totalW, maxR * 6) * 1.35;
  const c0 = cores[0];

  return (
    <div style={{ height, background: '#0f1420', borderRadius: 6, overflow: 'hidden' }}>
      <Canvas camera={{ position: [cam * 0.35, cam * 0.35, cam], fov: 40 }} dpr={[1, 2]}>
        <color attach="background" args={['#0f1420']} />
        <ambientLight intensity={0.9} />
        <directionalLight position={[maxR * 4, maxR * 6, maxR * 8]} intensity={1.1} />
        <directionalLight position={[-maxR * 4, 0, maxR * 4]} intensity={0.35} />
        <OrbitControls enablePan enableZoom />
        {cores.map((c, i) => (
          <Conductor key={i} core={c} x={-totalW / 2 + i * gap} len={len} annotate={i === 0} label={c.color || `Core ${i + 1}`} />
        ))}
        <Html position={[0, -maxR * 2.6, 0]} center>
          <div style={{ color: '#9fb3c8', fontSize: 11, fontFamily: 'monospace', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
            Ø {c0.outerD.toFixed(2)} mm · section {c0.coreSection.toFixed(2)} mm² · avg wall {c0.wall.avg.toFixed(3)} mm
          </div>
        </Html>
      </Canvas>
    </div>
  );
};

export default ConductorIsolation3D;
